/**
 * Parent organizations listing — `parent_organizations`, Phase 2 of the
 * first-class-entity migration. Mirrors the Movement Manufacturers tab: a
 * Firestore load (a couple of dozen documents, so no index), a local search, and
 * a row → editor flow.
 *
 * The brand and manufacturer counts come from the `parentOrg` facet on the two
 * entity indexes and resolve through each organization's legacy strings, since
 * brand and manufacturer docs don't point at these documents until Phase 3.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { ParentOrganization } from '../../types';
import { getParentOrganizations } from '../../services/firebase';
import { getParentOrgUsageCounts, isAlgoliaConfigured } from '../../services/algolia';
import { Button } from '../common/Button';
import { ParentOrganizationEditor } from './ParentOrganizationEditor';
import { SeedFromLookupDialog } from './SeedFromLookupDialog';
import { countParentOrgUsage, type ParentOrgUsage } from './parentOrgSeed';

/** Timeline and ownership-event counts — both stored inline, so free to compute. */
export function orgContentCounts(org: ParentOrganization): { timeline: number; ownership: number } {
  return {
    timeline: org.history?.length ?? 0,
    ownership: org.ownershipEvents?.length ?? 0,
  };
}

export function orgHasContent(org: ParentOrganization): boolean {
  const { timeline, ownership } = orgContentCounts(org);
  return timeline > 0 || ownership > 0;
}

interface Props {
  /** Open a brand's editor on the Brands tab. */
  onOpenBrand?: (brandId: string) => void;
  /** Open a manufacturer's editor on the Manufacturers tab. */
  onOpenManufacturer?: (manufacturerId: string) => void;
}

export function ParentOrganizations({ onOpenBrand, onOpenManufacturer }: Props = {}) {
  const [orgs, setOrgs] = useState<ParentOrganization[]>([]);
  const [usage, setUsage] = useState<ParentOrgUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ParentOrganization | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);
  /**
   * "Only orgs with content" — off by default. Filters in memory: `history` and
   * `ownershipEvents` are stored on the documents the listing already loaded, so
   * unlike the refs listing's image filter there is no index to ask.
   */
  const [onlyWithContent, setOnlyWithContent] = useState(false);
  const withContentCount = useMemo(() => orgs.filter(orgHasContent).length, [orgs]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrgs(await getParentOrganizations());
    } catch (err) {
      console.error('Failed to load parent organizations:', err);
      toast.error('Failed to load parent organizations');
    } finally {
      setLoading(false);
    }
    // Counts fail SOFT: a listing without them is still a working listing.
    if (isAlgoliaConfigured()) {
      try {
        setUsage(await getParentOrgUsageCounts());
      } catch (err) {
        console.warn('Parent-org usage counts unavailable:', err);
        setUsage(null);
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs
      .filter((o) => !onlyWithContent || orgHasContent(o))
      .filter((o) =>
        !q
          ? true
          : [o.name, o.displayName, o.id, o.country, ...(o.alternativeNames ?? [])]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(q)),
      )
      .map((o) => ({ org: o, counts: usage ? countParentOrgUsage(o, usage) : null }));
  }, [orgs, usage, search, onlyWithContent]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Parent Organizations</h2>
        <span className="text-sm text-gray-500">{orgs.length} total</span>
        <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={onlyWithContent}
            onChange={(e) => setOnlyWithContent(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Only show orgs with content
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
              withContentCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {withContentCount.toLocaleString()}
          </span>
        </label>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations…"
            className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Refresh
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSeedOpen(true)}
            title="Create documents from the parent_organization lookup — shows a dry run first"
          >
            Seed from lookup
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            + New organization
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center italic text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center italic text-gray-500">
          {orgs.length === 0
            ? 'No parent organizations yet — Seed from lookup creates them from the existing lookup.'
            : onlyWithContent
              ? 'No organizations with timeline or ownership events match.'
              : 'No organizations match your search.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Country</th>
                <th className="px-3 py-2 font-medium">Brands</th>
                <th className="px-3 py-2 font-medium">Manufacturers</th>
                <th className="px-3 py-2 font-medium">Content</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(({ org, counts }) => (
                <tr
                  key={org.id}
                  onClick={() => {
                    setEditing(org);
                    setEditorOpen(true);
                  }}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{org.displayName || org.name}</div>
                    <div className="font-mono text-[11px] text-gray-400">{org.id}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {org.country || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{counts ? counts.brands : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{counts ? counts.manufacturers : '—'}</td>
                  <td className="px-3 py-2">
                    <ContentChips org={org} />
                  </td>
                  <td className="px-3 py-2">
                    {org.status && org.status !== 'active' ? (
                      <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-medium capitalize text-gray-700">
                        {org.status}
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-400">active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ParentOrganizationEditor
        isOpen={editorOpen}
        org={editing}
        orgs={orgs}
        onClose={() => setEditorOpen(false)}
        onSaved={() => void load()}
        onDeleted={() => {
          setEditorOpen(false);
          void load();
        }}
        onOpenBrand={onOpenBrand}
        onOpenManufacturer={onOpenManufacturer}
        onOrgsChanged={() => void load()}
      />

      <SeedFromLookupDialog
        isOpen={seedOpen}
        onClose={() => setSeedOpen(false)}
        usage={usage}
        onSeeded={() => void load()}
      />
    </div>
  );
}

/**
 * Timeline and ownership-event counts, rendered only when non-zero so an empty
 * organization's row stays quiet and the ones with content stand out.
 */
function ContentChips({ org }: { org: ParentOrganization }) {
  const { timeline, ownership } = orgContentCounts(org);
  if (timeline === 0 && ownership === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {timeline > 0 && (
        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700">
          {timeline} timeline {timeline === 1 ? 'event' : 'events'}
        </span>
      )}
      {ownership > 0 && (
        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
          {ownership} ownership {ownership === 1 ? 'event' : 'events'}
        </span>
      )}
    </div>
  );
}
